import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  Input,
  AfterViewInit,
  OnDestroy,
  Signal,
} from '@angular/core';
import { RentalPoint } from 'src/app/models/rental-point.model';
import { LanguageService } from 'src/app/services/language.service';
import { FormService } from 'src/app/services/form.service';
import { LatLng } from 'leaflet';
import * as L from 'leaflet';

@Component({
  selector: 'app-map',
  standalone: true,
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss'
})
export class MapComponent implements AfterViewInit, OnDestroy {
  private languageService = inject(LanguageService);
  private formService = inject(FormService);

  @Input() height = 300;

  readonly mapId = 'map';

  private map: L.Map;
  private markers: Signal<RentalPoint[]>;
  private markerLayer: L.LayerGroup;

  private languageInitialized = false;
  private rentalPointsInitialized = false;

  constructor() {
    this.markers = computed(() => {
      const start = this.formService.startRentalPoints();
      const end = this.formService.endRentalPoints();

      const allPoints = [...start, ...end];
      const uniquePoints = allPoints.filter((point, index, self) =>
        self.findIndex(p => p.accountId === point.accountId) === index
      );

      return uniquePoints.filter(r => {
        const isValidLongitude = r.longitude && !isNaN(r.longitude);
        const isValidLatitude = r.latitude && !isNaN(r.latitude);
        return isValidLongitude && isValidLatitude;
      });
    });

    effect(() => {
        this.languageService.currentLanguage();
        if (!this.languageInitialized) {
          this.languageInitialized = true;
          return;
        }
        this.buildMarkers();
      },
    );

    effect(() => {
        this.formService.rentalPointsSignal();
        if (!this.rentalPointsInitialized) {
          this.rentalPointsInitialized = true;
          return;
        }
        this.buildMarkers();
      },
    );
  }

  @HostListener('window:resize')
  onResize() {
    setTimeout(() => {
      this.map.invalidateSize(true);
      this.map.getContainer().style.height = `calc(${this.height}px - 2px)`;
      this.centerMap();
    }, 400);
  }

  ngAfterViewInit() {
    if (document.getElementById(this.mapId)) {
      document.getElementById(this.mapId)!.style.height = `calc(${this.height}px - 2px)`;
    }
    this.initMap();
    this.buildMarkers();
  }

  private initMap() {
    this.map = L.map(this.mapId, {
      scrollWheelZoom: false,
      minZoom: 8,
      layers: [
        new L.TileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          'attribution': '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>'
        })
      ],
    });
    this.markerLayer = L.layerGroup().addTo(this.map);
  }

  private buildMarker(rentalPoint: RentalPoint) {
    const marker = L.marker(
      new LatLng(rentalPoint.latitude, rentalPoint.longitude), {
        icon: L.icon({iconUrl: 'assets/icons/icon-marker.png'}),
      }
    );
    const popupElement = document.createElement('div');
    popupElement.style.textAlign = 'center';
    const titleElem = `
      <strong>
          ${rentalPoint.translations[this.languageService.currentLanguage()] || rentalPoint.businessName}
      </strong>`;
    const subtitleElem = rentalPoint.city ? `<br>${rentalPoint.city}` : undefined;
    popupElement.innerHTML = subtitleElem ? `${titleElem}${subtitleElem}` : titleElem;
    marker.bindPopup(popupElement);
    return marker;
  }

  private buildMarkers() {
    this.markerLayer?.clearLayers();
    this.markers().forEach(m => this.buildMarker(m).addTo(this.map));
    this.centerMap();
  }

  private centerMap() {
    const bounds = L.latLngBounds(
      this.markers().map(m => new LatLng(m.latitude, m.longitude))
    );
    if (bounds.isValid()) {
      this.map.fitBounds(bounds);
    } else {
      this.map.setView([46.6135, 13.8365], 15);
    }
  }

  ngOnDestroy() {
    this.map.remove();
  }

}
